import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import ArtifactViz from '../../charts/ArtifactViz';
import { coerceMarkdownSource, fixLiteralMarkdownMarkers, hasUnresolvedMarkdown } from '../../../utils/markdownDisplay';

marked.setOptions({
  gfm: true,
  breaks: true,
});

const CHART_COLORS = [
  '#14b8a6', // teal
  '#38bdf8', // sky
  '#a78bfa', // violet
  '#fbbf24', // amber
  '#f472b6', // pink
  '#34d399', // emerald
  '#fb7185', // rose
  '#60a5fa', // blue
];

function sanitize(html) {
  return DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true } });
}

/** Pull ```testera-viz / ```viz fenced JSON blocks; keep document order for in-place charts. */
function splitMarkdownWithViz(markdown) {
  const src = String(markdown || '');
  const parts = [];
  const re = /```(?:testera-viz|viz)\s*\n([\s\S]*?)```/gi;
  let last = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) {
      parts.push({ type: 'md', text: src.slice(last, m.index) });
    }
    try {
      const parsed = JSON.parse(String(m[1] || '').trim());
      if (parsed && typeof parsed === 'object') {
        parts.push({ type: 'viz', data: parsed });
      } else {
        parts.push({ type: 'md', text: m[0] });
      }
    } catch {
      parts.push({ type: 'md', text: m[0] });
    }
    last = m.index + m[0].length;
  }
  if (last < src.length) parts.push({ type: 'md', text: src.slice(last) });
  if (!parts.length && src) parts.push({ type: 'md', text: src });
  return parts;
}

/** Parse GFM pipe tables from markdown text. */
function extractMarkdownTables(markdown) {
  const lines = String(markdown || '').split('\n');
  const tables = [];
  let i = 0;
  while (i < lines.length) {
    const header = lines[i];
    const sep = lines[i + 1];
    if (
      header &&
      sep &&
      /^\s*\|/.test(header) &&
      /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(sep)
    ) {
      const block = [header, sep];
      i += 2;
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        block.push(lines[i]);
        i += 1;
      }
      const rows = block
        .filter((line, idx) => idx !== 1)
        .map((line) =>
          line
            .trim()
            .replace(/^\|/, '')
            .replace(/\|$/, '')
            .split('|')
            .map((c) => c.trim())
        )
        .filter((cells) => cells.length >= 2 && cells.some(Boolean));
      if (rows.length >= 2) {
        tables.push({ headers: rows[0], rows: rows.slice(1) });
      }
      continue;
    }
    i += 1;
  }
  return tables;
}

function parseNumericCell(value) {
  const raw = String(value || '').replace(/,/g, '').trim();
  // Allow **148** / `12` / *3* wrapping from model output
  const unwrapped = raw
    .replace(/^\*\*(.+)\*\*$/, '$1')
    .replace(/^__(.+)__$/, '$1')
    .replace(/^`(.+)`$/, '$1')
    .replace(/^\*(.+)\*$/, '$1')
    .trim();
  if (!/^-?\d+(\.\d+)?%?$/.test(unwrapped)) return null;
  return parseFloat(unwrapped.replace('%', ''));
}

/** Strip common inline markdown so chart labels don't show literal **bold**. */
function stripInlineMarkdown(text) {
  let s = String(text || '').trim();
  if (!s) return '';
  // Repeat a few times for nested / leftover markers
  for (let i = 0; i < 3; i += 1) {
    const next = s
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/_(.+?)_/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .replace(/~~(.+?)~~/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    if (next === s) break;
    s = next;
  }
  // Orphan emphasis markers the model left around a whole cell
  return s.replace(/^\*+|\*+$/g, '').replace(/^_+|_+$/g, '').trim();
}

/** Prefer 2-column label/count tables for charts. */
function pickChartSeries(tables) {
  for (const table of tables) {
    if (!table.headers || table.headers.length < 2 || table.rows.length < 2) continue;
    const series = [];
    for (const row of table.rows) {
      const label = stripInlineMarkdown(row[0]);
      let value = null;
      for (let c = 1; c < row.length; c += 1) {
        value = parseNumericCell(row[c]);
        if (value !== null) break;
      }
      if (!label || value === null || Number.isNaN(value)) continue;
      series.push({ label, value });
    }
    if (series.length >= 2) {
      return {
        title: stripInlineMarkdown(table.headers[0] || 'Breakdown') || 'Breakdown',
        series,
      };
    }
  }
  return null;
}

function BarChart({ title, series }) {
  const max = Math.max(...series.map((s) => s.value), 1);
  const total = series.reduce((sum, s) => sum + (s.value > 0 ? s.value : 0), 0);

  return (
    <div className="mt-3 mb-1 rounded-xl border border-border/80 bg-surface-1/80 p-3">
      <div className="flex items-baseline justify-between gap-2 mb-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">{title}</p>
        {total > 0 && (
          <p className="text-[11px] text-text-muted tabular-nums">n={total.toLocaleString()}</p>
        )}
      </div>
      <div className="space-y-2">
        {series.map((item, idx) => {
          const pct = Math.max(2, Math.round((item.value / max) * 100));
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          return (
            <div key={`${item.label}-${idx}`} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] gap-2 items-center">
              <div className="text-[12px] text-text truncate" title={item.label}>{item.label}</div>
              <div className="h-2.5 rounded-full bg-surface-3 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: color }}
                />
              </div>
              <div className="text-[12px] font-medium tabular-nums text-text text-right min-w-[2.5rem]">
                {Number.isInteger(item.value) ? item.value.toLocaleString() : item.value}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DonutChart({ title, series }) {
  const positive = series.filter((s) => s.value > 0);
  if (positive.length < 2) return null;
  const total = positive.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return null;

  const size = 120;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="mt-3 rounded-xl border border-border/80 bg-surface-1/80 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-2">{title} mix</p>
      <div className="flex items-center gap-3">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            {positive.map((item, idx) => {
              const len = (item.value / total) * c;
              const dashOffset = -offset;
              offset += len;
              return (
                <circle
                  key={`${item.label}-arc-${idx}`}
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                  strokeWidth={stroke}
                  strokeDasharray={`${len} ${c - len}`}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="butt"
                />
              );
            })}
          </g>
          <text
            x="50%"
            y="48%"
            textAnchor="middle"
            className="fill-text"
            style={{ fontSize: 16, fontWeight: 700 }}
          >
            {total.toLocaleString()}
          </text>
          <text
            x="50%"
            y="62%"
            textAnchor="middle"
            className="fill-text-muted"
            style={{ fontSize: 9 }}
          >
            total
          </text>
        </svg>
        <ul className="space-y-1 min-w-0 flex-1">
          {positive.slice(0, 8).map((item, idx) => (
            <li key={`${item.label}-leg-${idx}`} className="flex items-center gap-2 text-[11px] min-w-0">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ background: CHART_COLORS[idx % CHART_COLORS.length] }}
              />
              <span className="truncate text-text">{item.label}</span>
              <span className="ml-auto tabular-nums text-text-muted">
                {Math.round((item.value / total) * 100)}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function parseArtifactHref(href) {
  try {
    const raw = String(href || '').trim();
    if (!raw) return null;
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://app.testera.io');
    const tab = url.searchParams.get('tab');
    const docId = url.searchParams.get('docId');
    if (tab === 'documents' && docId && /^[0-9a-fA-F]{24}$/.test(docId)) {
      return { id: docId, openPath: `/?tab=documents&docId=${docId}` };
    }
  } catch { /* ignore */ }
  return null;
}

function scrubVizLabels(block) {
  if (!block || typeof block !== 'object') return block;
  const next = { ...block };
  if (typeof next.title === 'string') next.title = stripInlineMarkdown(next.title);
  const scrubSeries = (arr) => {
    if (!Array.isArray(arr)) return arr;
    return arr.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const label = item.label ?? item.name ?? item.x;
      if (typeof label !== 'string') return item;
      const cleaned = stripInlineMarkdown(label);
      return {
        ...item,
        ...(item.label != null ? { label: cleaned } : {}),
        ...(item.name != null ? { name: cleaned } : {}),
        ...(item.x != null && typeof item.x === 'string' ? { x: cleaned } : {}),
      };
    });
  };
  if (Array.isArray(next.series)) next.series = scrubSeries(next.series);
  if (Array.isArray(next.data)) next.data = scrubSeries(next.data);
  return next;
}

const PROSE_CLASSES = [
  '[&>:first-child]:mt-0 [&>:last-child]:mb-0',
  '[&_p]:my-2',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1',
  '[&_li]:my-0.5',
  '[&_strong]:font-semibold [&_strong]:text-text',
  '[&_em]:italic',
  '[&_a]:text-teal-400 [&_a]:underline [&_a]:cursor-pointer',
  '[&_code]:rounded [&_code]:bg-surface-3 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px] [&_code]:font-mono',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-surface-3 [&_pre]:p-2.5 [&_pre]:text-[12px]',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1.5',
  '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1.5',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2.5 [&_h3]:mb-1',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-teal-500/50 [&_blockquote]:pl-3 [&_blockquote]:text-text-muted',
  '[&_hr]:my-3 [&_hr]:border-border',
  '[&_table]:my-2.5 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[12px]',
  '[&_th]:border [&_th]:border-border [&_th]:bg-surface-3/80 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1.5',
  '[&_tr:nth-child(even)_td]:bg-surface-1/40',
].join(' ');

/**
 * Rich markdown renderer for AI Companion replies and Artifacts notes (GFM +
 * in-place ```testera-viz``` charts; optional auto-chart from numeric tables).
 */
export default function CompanionMarkdown({
  content,
  onOpenArtifact,
  className = '',
  compactCharts = true,
  autoTableCharts = true,
}) {
  const segments = useMemo(() => {
    const parts = splitMarkdownWithViz(String(content || ''));
    const mdJoined = parts.filter((p) => p.type === 'md').map((p) => p.text).join('\n');
    const tables = autoTableCharts ? extractMarkdownTables(mdJoined) : [];
    const chartSeries = autoTableCharts ? pickChartSeries(tables) : null;
    const vizCount = parts.filter((p) => p.type === 'viz').length;
    return {
      parts: parts.map((p) => {
        if (p.type === 'viz') return { type: 'viz', data: scrubVizLabels(p.data) };
        const src = coerceMarkdownSource(p.text || '');
        let parsed = marked.parse(src, { async: false });
        if (typeof parsed !== 'string') parsed = String(parsed || '');
        if (hasUnresolvedMarkdown(parsed)) parsed = fixLiteralMarkdownMarkers(parsed);
        return { type: 'md', html: sanitize(parsed) };
      }),
      fallbackChart: vizCount === 0 ? chartSeries : null,
    };
  }, [content, autoTableCharts]);

  const onClick = (e) => {
    const a = e.target?.closest?.('a');
    if (!a) return;
    const artifact = parseArtifactHref(a.getAttribute('href'));
    if (!artifact || typeof onOpenArtifact !== 'function') return;
    e.preventDefault();
    onOpenArtifact(artifact);
  };

  return (
    <div className={`companion-md text-[13px] leading-relaxed text-text ${className}`.trim()} onClick={onClick}>
      {segments.parts.map((part, i) => {
        if (part.type === 'viz') {
          return (
            <div key={`viz-${i}`} className="my-3">
              <ArtifactViz data={{ ...part.data, createdByAi: true }} compact={compactCharts} />
            </div>
          );
        }
        if (!part.html || part.html === '') return null;
        return (
          <div
            key={`md-${i}`}
            className={PROSE_CLASSES}
            dangerouslySetInnerHTML={{ __html: part.html }}
          />
        );
      })}
      {segments.fallbackChart && (
        <>
          <BarChart title={segments.fallbackChart.title} series={segments.fallbackChart.series} />
          {segments.fallbackChart.series.length <= 8 && (
            <DonutChart title={segments.fallbackChart.title} series={segments.fallbackChart.series} />
          )}
        </>
      )}
    </div>
  );
}
